import type { OdinModuleId } from './metadata';

export type OdinScoringTiming = 'end-game' | 'mid-game' | 'both' | 'none';
export type OdinScoringSourceStatus =
  | 'verified'
  | 'component-dependent'
  | 'partial'
  | 'blocked';

export type OdinScoringRecord = {
  id: string;
  gameId: 'a-feast-for-odin';
  moduleId: OdinModuleId;
  category: 'base' | 'expansion' | 'solo';
  element: string;
  timing: OdinScoringTiming;
  scoring: string;
  conditions: string;
  exceptions: string;
  returnsMeeple: string;
  sourceStatus: OdinScoringSourceStatus;
  verified: boolean;
  order: number;
  keywords: string[];
};

/**
 * Structured end-game / category scoring for Uczta dla Odyna.
 * Printed sheet values and icon catalogues stay component-dependent / PARTIAL.
 */
export const odinScoringRecords: OdinScoringRecord[] = [
  {
    id: 'odin-sheet-positive',
    gameId: 'a-feast-for-odin',
    moduleId: 'base',
    category: 'base',
    element: 'Dodatnie kategorie notesu',
    timing: 'end-game',
    scoring:
      'Zsumuj dodatnie wartości kategorii notesu: łodzie, emigracja, wyspy i odkrycia, budynki, zwierzęta, karty pomocników dające punkty, srebro i inne elementy wskazane w arkuszu, dodatkowe żetony punktów — zgodnie z wartościami nadrukowanymi / na notesie.',
    conditions: 'Po zakończeniu ostatniej rundy i wszystkich jej faz.',
    exceptions: 'Każdy element policz tylko w jednej właściwej kategorii.',
    returnsMeeple: 'N/A',
    sourceStatus: 'component-dependent',
    verified: true,
    order: 10,
    keywords: ['notes', 'łodzie', 'emigracja', 'budynki', 'zwierzęta'],
  },
  {
    id: 'odin-negative-fields',
    gameId: 'a-feast-for-odin',
    moduleId: 'base',
    category: 'base',
    element: 'Ujemne pola',
    timing: 'end-game',
    scoring:
      'Odejmij wszystkie widoczne ujemne pola na planszy osady, wyspach i budynkach oraz inne kary — zgodnie z wartością nadrukowaną na polu.',
    conditions: 'Pole ujemne punktuje tylko gdy jest widoczne (niezakryte).',
    exceptions:
      'Zakrycie pola usuwa karę; samo zakrycie nie daje dodatnich punktów.',
    returnsMeeple: 'N/A',
    sourceStatus: 'component-dependent',
    verified: true,
    order: 20,
    keywords: ['minus', 'ujemne', 'kara'],
  },
  {
    id: 'odin-goods-tiles',
    gameId: 'a-feast-for-odin',
    moduleId: 'base',
    category: 'base',
    element: 'Kafel dobra na planszy',
    timing: 'end-game',
    scoring:
      'Zwykle 0 jako osobna kategoria VP — znaczenie polega na zakryciu kary, uruchomieniu dochodu albo premii.',
    conditions: 'Kafel leży na planszy przestrzennej.',
    exceptions: 'Nie doliczaj punktów „bo ładne” z wartości nadruku kafla poza regułą notesu.',
    returnsMeeple: 'N/A',
    sourceStatus: 'verified',
    verified: true,
    order: 30,
    keywords: ['kafel', 'dobra', 'zakrycie'],
  },
  {
    id: 'odin-tie',
    gameId: 'a-feast-for-odin',
    moduleId: 'base',
    category: 'base',
    element: 'Remis',
    timing: 'end-game',
    scoring:
      'Remis rozstrzygnij zgodnie z oficjalnym kryterium wydania / notesu. Aplikacja nie gwarantuje kryterium — przy braku źródła pozostaw remisujących na równorzędnym miejscu.',
    conditions: 'Równy wynik po sumowaniu notesu i minusów.',
    exceptions: 'Nie wymyślaj nowego tie-breakera.',
    returnsMeeple: 'N/A',
    sourceStatus: 'blocked',
    verified: false,
    order: 40,
    keywords: ['remis', 'tie'],
  },
  {
    id: 'odin-norwegians-vp-tokens',
    gameId: 'a-feast-for-odin',
    moduleId: 'norwegians',
    category: 'expansion',
    element: 'Żetony PZ (Norwegowie)',
    timing: 'end-game',
    scoring:
      'Żetony punktów zwycięstwa z dodatku liczą się do wyniku końcowego zgodnie z notesem / żetonami — wartość nadrukowana na żetonie.',
    conditions: 'Moduł Norwegowie włączony.',
    exceptions:
      'Punkty nadrukowane na łodzi/zwierzęciu na szopie rzemieślniczej nie liczą się osobno (Norwegowie).',
    returnsMeeple: 'N/A',
    sourceStatus: 'component-dependent',
    verified: true,
    order: 100,
    keywords: ['żeton', 'PZ', 'norwegowie'],
  },
  {
    id: 'odin-solo-records',
    gameId: 'a-feast-for-odin',
    moduleId: 'solo',
    category: 'solo',
    element: 'Solo — porównanie rekordów',
    timing: 'end-game',
    scoring:
      'Porównuj rekordy przy tej samej konfiguracji (nie ma osobnej tabeli VP solo w aplikacji).',
    conditions: 'Wariant solo włączony.',
    exceptions: '—',
    returnsMeeple: 'N/A',
    sourceStatus: 'partial',
    verified: true,
    order: 200,
    keywords: ['solo', 'rekord'],
  },
];

export function filterOdinScoring(
  activeModuleIds: OdinModuleId[],
  options: { showAll?: boolean } = {}
): OdinScoringRecord[] {
  if (options.showAll) {
    return odinScoringRecords.slice().sort((a, b) => a.order - b.order);
  }
  const enabled = new Set(activeModuleIds);
  enabled.add('base');
  return odinScoringRecords
    .filter((r) => enabled.has(r.moduleId))
    .sort((a, b) => a.order - b.order);
}

export function validateOdinScoringTables(): string[] {
  const issues: string[] = [];
  const ids = new Set<string>();
  for (const r of odinScoringRecords) {
    if (ids.has(r.id)) issues.push(`Duplicate odin scoring id ${r.id}`);
    ids.add(r.id);
    if (!r.element.trim() || !r.scoring.trim()) {
      issues.push(`Incomplete odin scoring ${r.id}`);
    }
  }
  const baseOnly = filterOdinScoring(['base']);
  if (!baseOnly.every((r) => r.moduleId === 'base')) {
    issues.push('Base filter leaked non-base odin rows');
  }
  if (!baseOnly.some((r) => r.id === 'odin-sheet-positive')) {
    issues.push('Missing base sheet scoring');
  }
  const withNor = filterOdinScoring(['base', 'norwegians']);
  if (!withNor.some((r) => r.id === 'odin-norwegians-vp-tokens')) {
    issues.push('Norwegians scoring missing when enabled');
  }
  if (withNor.some((r) => r.moduleId === 'solo')) {
    issues.push('Solo scoring visible without solo module');
  }
  return issues;
}
